import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';
import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useContextSelector } from 'use-context-selector';

import { setLoginShownAction } from '@/core/store/global/app-view-reducers';
import { AppContext, setAppState } from '@/core/store/global/AppProvider';
import { appSettings } from '@/settings/app-settings';

type EmailState = {
  email: string;
};

const WaitList: React.FC = () => {
  const [emailState, setEmailState] = useState<EmailState>({ email: '' });
  const [showErrorMessage, setShowErrorMessage] = useState('');
  const [showEmailValidation, setShowEmailValidation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validateRecaptcha, setValidateRecaptcha] = useState(false);
  const [validateRecaptchaMessage, setValidateRecaptchaMessage] = useState(false);
  const googleSiteKey = appSettings.api.GOOGLECAPTCHASITEKEY;
  const [isValidMail, setIsValidMail] = useState<boolean>(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9-]+$/;
    setIsValidMail(emailRegex.test(e.target.value));
    setEmailState({ ...emailState, email: e.target.value });
  };
  const dispatch = useContextSelector(AppContext, setAppState);
  function handleOnCaptchaChange() {
    setValidateRecaptcha(true);
    setValidateRecaptchaMessage(false);
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    const data = {
      email: emailState.email,
    };

    if (!data.email || !/\S+@\S+\.\S+/.test(data.email)) {
      setShowEmailValidation(true);
      setTimeout(() => {
        setShowEmailValidation(false);
      }, 3000);
      return;
    }

    if (!validateRecaptcha) {
      setValidateRecaptchaMessage(true);
      return;
    }

    setIsLoading(true);

    const host = 'https://wishlist-api.dev.bldr.it/';
    try {
      const response = await fetch(`${host}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        },
        body: JSON.stringify(data),
        // credentials: 'omit'
      });

      response.json().then((data: any) => {
        if (!data.error) {
          setEmailState({ email: '' });
          setIsLoading(false);
          setShowSuccess(true);
          setIsValidMail(false);
        } else {
          setIsLoading(false);
          setShowErrorMessage(data.error);
          console.log();

          setTimeout(() => {
            setShowErrorMessage('');
          }, 3000);
        }
      });
    } catch (error) {
      setIsLoading(false);

      console.error('Sign up failed:', error);
    }
  };

  const onBack = () => {
    dispatch(setLoginShownAction({ isLoginShown: false }));
  };

  return (
    <>
      <div className="flex flex-wrap w-full mt-6 ">
        <div className="relative w-full p-2  border border-transparent rounded-xl ">
          {showSuccess ? (
            <>
              <div className="flex flex-col items-center justify-center h-auto   ">
                <div className="animate-pulse">
                  <CheckCircleIcon className="w-12 h-12 text-green-500 mb-4" />
                </div>
                <h2 className="text-2xl font-bold text-black mb-2">Hurry! You are in a waitlist</h2>
                <p className="text-black text-center">We will update you soon with more information.</p>
              </div>
              <div className="flex justify-center md:flex-row items-center mt-2">
                <button
                  className="flex-none rounded-full py-2.5 px-3.5 text-sm font-semibold text-black border border-black shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                  onClick={onBack}
                >
                  BACK
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="mx-auto max-w-2xl text-center text-3xl font-bold tracking-tight text-black sm:text-4xl">
                {"Get notified when we're launching"}
              </h2>

              <form className="mx-auto mt-5 flex flex-col w-full max-w-[400px] gap-y-8 border border-gray-200 py-8 px-6 rounded-xl">
                <div className="flex flex-col w-full">
                  <label htmlFor="email-address" className="sr-only">
                    Email address
                  </label>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                      Email
                    </label>
                    <div className="mt-2">
                      <input
                        type="email"
                        name="email"
                        id="email"
                        className="block w-full border-gray-300 rounded-md border px-2 py-1.5 text-gray-900 shadow-sm ring-transparent placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                        placeholder="you@example.com"
                        aria-describedby="email-description"
                        onChange={handleInputChange}
                        value={emailState.email}
                        autoComplete="off"
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-600" id="email-description">
                      We will use it to inform you about the launch.
                    </p>
                  </div>
                </div>

                <div className="items-center text-center mx-auto w-full flex flex-col">
                  <ReCAPTCHA
                    sitekey={googleSiteKey || ''}
                    onChange={handleOnCaptchaChange}
                    className="justify-center items-center"
                  />
                </div>
                {showErrorMessage && (
                  <p
                    className="mt-6 left-0 right-0 text-sm text-red-600 text-center"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ marginRight: '5px' }}>⚠</span>
                    {showErrorMessage}
                  </p>
                )}
                {showEmailValidation && (
                  <p
                    className="left-0 mt-6 right-0 text-sm text-red-600 text-center"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <XCircleIcon className="h-5 w-5 mr-2 text-red-400" aria-hidden="true" />
                    Enter the valid email.
                  </p>
                )}
                {validateRecaptchaMessage && (
                  <div className="mt-6 flex justify-center">
                    <div className="ml-0">
                      <p className="text-sm font-medium text-red-600">
                        <span style={{ marginRight: '5px' }}>⚠</span>
                        Please verify that you are not a robot.
                      </p>
                    </div>
                  </div>
                )}

                <div className="w-full flex flex-col">
                  <button
                    type="submit"
                    className={`flex-none rounded-full py-2  text-white shadow-sm  focus-visible:outline focus-visible:outline-2  focus-visible:outline-offset-2 focus-visible:outline-white 
      ${isLoading ? 'animate-pulse' : ''}
      ${isValidMail ? 'bg-black hover:bg-slate-900' : 'bg-slate-400 pointer-events-none'}
      `}
                    disabled={isLoading}
                    onClick={handleSubmit}
                  >
                    {isLoading ? 'Loading...' : 'Notify me'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
};
export function WishListFooter({ onLogin = () => {} }) {
  return (
    <div>
      <div className="flex flex-row justify-center items-center space-x-1">
        <span>Existing member?</span>
        <button
          className={['font-bold', 'bg-transparent outline-none border-none shadow-none'].join(' ')}
          onClick={(event) => {
            event.preventDefault();
            onLogin();
          }}
        >
          <span className="h-6">Login</span>
        </button>
      </div>
    </div>
  );
}
export default WaitList;
